/**
 * Settings block to re-trigger the onboarding: replay the guided tour and
 * re-enable the per-section help boxes after they've been dismissed. Rendered
 * inside ProfileSettings; visible to anyone with an onboarding audience.
 */
import { Button } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Compass, Sparkles } from "lucide-react";

import { useDemoTourStore } from "./demoTourStore";
import { useSectionGuideStore } from "./sectionGuideStore";
import { onboardingKey, useOnboardingMode } from "./useOnboardingMode";

export function OnboardingSettings(): React.ReactElement | null {
  const { i18n } = useLingui();
  const start = useDemoTourStore((s) => s.start);
  const restore = useSectionGuideStore((s) => s.restore);
  const mode = useOnboardingMode();

  if (!mode) return null;
  const key = onboardingKey();

  return (
    <section className="mt-8 border-hairline border-t pt-6">
      <h3 className="font-semibold text-[13px] text-ink-1">
        <Trans>Aiuto e onboarding</Trans>
      </h3>
      <p className="mt-1 text-[12px] text-ink-3 leading-relaxed">
        <Trans>
          Rivedi il giro guidato di Ando o riattiva i riquadri d'aiuto nelle
          sezioni se li avevi nascosti.
        </Trans>
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => start(key)}>
          <Compass size={14} strokeWidth={1.75} aria-hidden className="mr-1.5" />
          {i18n._(msg`Rivedi il tour guidato`)}
        </Button>
        <Button variant="outline" size="sm" onClick={() => restore(key)}>
          <Sparkles size={14} strokeWidth={1.75} aria-hidden className="mr-1.5" />
          {i18n._(msg`Riattiva gli aiuti delle sezioni`)}
        </Button>
      </div>
    </section>
  );
}

OnboardingSettings.displayName = "OnboardingSettings";
